import { useCallback, useEffect, useRef, useState } from 'react';
import Hls, { Events, type MediaFragment } from 'hls.js';
import { getCurrentFirebaseToken } from '../lib/api/client';
import { freshRecovery, nextRecovery, type RecoveryState } from '../utils/hlsRecovery';

interface ProgramFragment {
  start: number;
  duration: number;
  programDateTime: number;
}

interface UseHlsPlayerOptions {
  /** Instante UTC a reproducir cuando la playlist VOD termina de cargarse. */
  seekToProgramDateTime?: string | null;
}

interface HlsPlayer {
  videoRef: React.RefObject<HTMLVideoElement>;
  error: string | null;
  /**
   * `true` mientras el reproductor se recupera de un error fatal (#148): un
   * corte normal del proveedor no es un fallo y la UI debe decir
   * «reconectando», no «error». Vuelve a `false` cuando entra un segmento.
   */
  isReconnecting: boolean;
  isSupported: boolean;
  playingProgramDateTime: string | null;
  seekToProgramDateTime: (value: string) => boolean;
}

function isSameOriginApiRequest(url: string): boolean {
  const parsed = new URL(url, window.location.origin);
  return parsed.origin === window.location.origin && parsed.pathname.startsWith('/v1/');
}

function playerError(type: string, details: string): string {
  if (type === 'networkError') return 'No se pudo cargar la transmisión de video.';
  if (type === 'mediaError') return 'El navegador no pudo decodificar este video.';
  return `El reproductor de video falló (${details}).`;
}

/**
 * Player HLS desechable para el vivo diferido y el histórico. Las playlists
 * de Core se autorizan con Firebase; los fragmentos firmados de GCS quedan
 * deliberadamente sin Bearer, aun si hls.js los descarga por XHR.
 */
export function useHlsPlayer(source: string | null, options: UseHlsPlayerOptions = {}): HlsPlayer {
  const videoRef = useRef<HTMLVideoElement>(null);
  const fragmentsRef = useRef<ProgramFragment[]>([]);
  const desiredProgramDateTimeRef = useRef(options.seekToProgramDateTime ?? null);
  const currentFragmentRef = useRef<ProgramFragment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [playingProgramDateTime, setPlayingProgramDateTime] = useState<string | null>(null);

  desiredProgramDateTimeRef.current = options.seekToProgramDateTime ?? null;

  const seekToProgramDateTime = useCallback((value: string): boolean => {
    const video = videoRef.current;
    const target = new Date(value).getTime();
    if (!video || Number.isNaN(target)) return false;

    const fragment = fragmentsRef.current.find(candidate => (
      target >= candidate.programDateTime
      && target < candidate.programDateTime + candidate.duration * 1000
    )) ?? fragmentsRef.current.find(candidate => candidate.programDateTime >= target);

    if (!fragment) return false;
    const offsetSeconds = Math.max(0, Math.min(
      fragment.duration,
      (target - fragment.programDateTime) / 1000,
    ));
    video.currentTime = fragment.start + offsetSeconds;
    return true;
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    setError(null);
    setIsReconnecting(false);
    setPlayingProgramDateTime(null);
    fragmentsRef.current = [];
    currentFragmentRef.current = null;

    if (!source) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      return undefined;
    }

    if (!Hls.isSupported()) {
      setIsSupported(false);
      return undefined;
    }
    setIsSupported(true);

    let destroyed = false;
    const hls = new Hls({
      // hls.js espera esta promesa antes de abrir el XHR. Renovar el token por
      // reload de playlist evita asumir que la sesión de Firebase dura lo mismo
      // que la sesión de video.
      xhrSetup: async (xhr, url) => {
        if (isSameOriginApiRequest(url)) {
          const token = await getCurrentFirebaseToken();
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }
      },
    });

    const rememberFragments = (fragments: MediaFragment[]) => {
      fragmentsRef.current = fragments.flatMap(fragment => {
        const programDateTime = fragment.programDateTime;
        return programDateTime === null ? [] : [{
          start: fragment.start,
          duration: fragment.duration,
          programDateTime,
        }];
      });
      const requested = desiredProgramDateTimeRef.current;
      if (requested) seekToProgramDateTime(requested);
    };

    let isLive = false;
    let recovery: RecoveryState = freshRecovery();
    let recoveryTimer: ReturnType<typeof setTimeout> | null = null;

    hls.on(Events.LEVEL_LOADED, (_event, data) => {
      isLive = data.details.live;
      rememberFragments(data.details.fragments);
    });
    // Un segmento en el buffer = la recuperación funcionó (o nunca hizo
    // falta): se reinicia el contador para que un corte cada minuto no
    // acumule hasta rendirse, y la UI deja de decir «reconectando».
    hls.on(Events.FRAG_BUFFERED, () => {
      recovery = freshRecovery();
      setIsReconnecting(false);
    });
    hls.on(Events.FRAG_CHANGED, (_event, data) => {
      const programDateTime = data.frag.programDateTime;
      currentFragmentRef.current = programDateTime === null ? null : {
        start: data.frag.start,
        duration: data.frag.duration,
        programDateTime,
      };
    });
    // #148: antes, cualquier error fatal destruía el reproductor y nadie lo
    // volvía a arrancar — con el vivo cortándose cada ~40 s (#147) quedaba
    // congelado en los ~20 s ya cargados aunque el grabador volviera a
    // publicar. Ahora se recupera con backoff y tope (utils/hlsRecovery.ts);
    // el error solo se muestra cuando la recuperación se agota.
    hls.on(Events.ERROR, (_event, data) => {
      if (!data.fatal || destroyed) return;
      const { action, state } = nextRecovery(recovery, data.type);
      recovery = state;
      if (action.kind === 'give-up') {
        setIsReconnecting(false);
        setError(playerError(data.type, data.details));
        hls.destroy();
        return;
      }
      setIsReconnecting(true);
      if (recoveryTimer) clearTimeout(recoveryTimer);
      recoveryTimer = setTimeout(() => {
        recoveryTimer = null;
        if (destroyed) return;
        if (action.kind === 'reload-network') {
          // En vivo, la sesión nueva del grabador no comparte segmentos con
          // la anterior: seguir «donde se quedó» no sirve, se salta al borde
          // del directo (-1). En VOD se retoma en el instante actual.
          hls.startLoad(isLive ? -1 : video.currentTime);
          return;
        }
        if (action.swapAudioCodec) hls.swapAudioCodec();
        hls.recoverMediaError();
      }, action.delayMs);
    });

    const syncProgramDateTime = () => {
      const fragment = currentFragmentRef.current;
      if (!fragment) return;
      const value = fragment.programDateTime + (video.currentTime - fragment.start) * 1000;
      setPlayingProgramDateTime(new Date(value).toISOString());
    };
    video.addEventListener('timeupdate', syncProgramDateTime);

    hls.loadSource(source);
    hls.attachMedia(video);

    return () => {
      destroyed = true;
      if (recoveryTimer) clearTimeout(recoveryTimer);
      video.removeEventListener('timeupdate', syncProgramDateTime);
      hls.destroy();
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [source, seekToProgramDateTime]);

  // LEVEL_LOADED cubre el arranque. Este efecto cubre un clic posterior
  // sobre la línea de tiempo cuando la playlist VOD ya tiene sus fragmentos.
  useEffect(() => {
    if (options.seekToProgramDateTime) seekToProgramDateTime(options.seekToProgramDateTime);
  }, [options.seekToProgramDateTime, seekToProgramDateTime]);

  return { videoRef, error, isReconnecting, isSupported, playingProgramDateTime, seekToProgramDateTime };
}
